// "Alerts on this phone" — the driver-facing switch for push notifications.
//
// Shown on the Customer Service tab. Loud while alerts are OFF (a driver who never turns
// them on gets no banner, and would never know why), one quiet line once they're on.
//
// On iPhone the honest answer is often "you can't yet" — iOS only allows Web Push for a
// web app opened from the Home Screen icon. Rather than offer an Enable button that fails,
// this shows the two-step Add to Home Screen instruction, which is the actual fix.
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Share, CheckCircle, AlertCircle } from 'lucide-react';
import { alertStatus, enableAlerts, disableAlerts } from '../utils/pushAlerts';

const TILE = { driver: 'HP Driver', engine: 'HP Engine' };
const HEADING = { driver: 'Get told when a case lands', engine: 'Get told when a case needs the office' };
const BLURB = {
  driver: 'Turn this on and your phone shows an alert the moment a customer case is put to you or the office replies — plus a red number on the app icon while one is still waiting.',
  engine: 'Turn this on and your phone shows an alert when a case lands from Australia Post, when a driver answers one, when AP re-opens a case you had finished, and when cases are still unanswered at 2pm — plus a red number on the app icon for whatever is waiting on the office.',
};

const card = (accent) => ({
  border: `1px solid ${accent || 'var(--hp-border)'}`, borderRadius: 'var(--radius)',
  background: 'var(--hp-surface)', padding: 14, marginBottom: 12,
});
const btn = (primary) => ({
  padding: '9px 14px', borderRadius: 8, border: primary ? 'none' : '1px solid var(--hp-border)',
  background: primary ? 'var(--hp-gold)' : 'transparent', color: primary ? 'var(--hp-black)' : 'var(--hp-dim)',
  fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
});

// `app`: which Home Screen app this card is running in — 'driver' or 'engine'.
export default function PushAlertsCard({ app = 'driver' }) {
  const [st, setSt] = useState(null);       // null = still checking
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const refresh = useCallback(() => { alertStatus().then(setSt).catch(() => setSt({ state: 'unsupported' })); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const turnOn = async () => {
    setBusy(true); setMsg('');
    const r = await enableAlerts(app);
    setBusy(false);
    if (r.ok) { setMsg('Alerts are on for this phone.'); refresh(); }
    else if (r.state === 'denied') setMsg(`Your phone is blocking alerts. Turn them back on in Settings → Notifications → ${TILE[app]}, then try again.`);
    else if (r.state === 'dismissed') setMsg('No problem — tap Turn on alerts whenever you want them.');
    else setMsg(r.error || 'Could not turn on alerts.');
  };

  const turnOff = async () => {
    setBusy(true); setMsg('');
    await disableAlerts();
    setBusy(false); setMsg('Alerts are off on this phone.'); refresh();
  };

  const test = async () => {
    setBusy(true); setMsg('');
    const r = await fetch('/.netlify/functions/push-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'test' }),
    }).then((x) => x.json()).catch(() => ({}));
    setBusy(false);
    setMsg(r.success ? 'Sent. The banner should appear in a moment, and the app icon should show a 3 — that is a demo number, it goes back to your real case count next time you open this screen.' : 'Nothing went out. Try turning alerts off and on again.');
  };

  if (!st) return null;                                    // still checking — say nothing
  if (st.state === 'unsupported') return null;             // old browser: no switch to offer

  // iPhone in a Safari tab. The Enable button cannot work here, so give the real fix.
  if (st.state === 'needsInstall') {
    return (
      <div style={card('var(--hp-gold)')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-gold)', marginBottom: 6, fontSize: '0.92rem' }}>
          <Bell size={16} /> {HEADING[app]}
        </div>
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', lineHeight: 1.5 }}>
          Your iPhone can pop up an alert and put a red number on the app icon — but only once
          this {app === 'engine' ? 'page' : 'portal'} is on your Home Screen. It takes ten seconds:
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-text)' }}>
            <Share size={15} style={{ color: 'var(--hp-gold)' }} />
            <span><strong>1.</strong> Tap the Share button at the bottom of Safari</span>
          </div>
          <div style={{ marginTop: 4, color: 'var(--hp-text)' }}><strong>2.</strong> Choose <strong>Add to Home Screen</strong></div>
          <div style={{ marginTop: 4, color: 'var(--hp-text)' }}><strong>3.</strong> Open the portal from that new icon and come back here</div>
        </div>
      </div>
    );
  }

  if (st.state === 'denied') {
    return (
      <div style={card()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-dim)', fontSize: '0.85rem' }}>
          <BellOff size={15} /> Alerts are blocked on this phone. Settings → Notifications → {TILE[app]} to allow them.
        </div>
      </div>
    );
  }

  // Registered: one quiet line, plus a way to prove it works and a way out.
  if (st.on) {
    return (
      <div style={card()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <CheckCircle size={15} style={{ color: 'var(--hp-green, #4caf50)' }} />
          <span style={{ color: 'var(--hp-text)', fontSize: '0.85rem', fontWeight: 600 }}>Alerts are on for this phone</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button style={btn(false)} onClick={test} disabled={busy}>Send a test</button>
            <button style={btn(false)} onClick={turnOff} disabled={busy}>Turn off</button>
          </div>
        </div>
        {msg && <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', marginTop: 8 }}>{msg}</div>}
      </div>
    );
  }

  return (
    <div style={card('var(--hp-gold)')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-gold)', marginBottom: 6, fontSize: '0.92rem' }}>
        <Bell size={16} /> Get told when a case lands
      </div>
      <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: 10 }}>
        Turn this on and your phone shows an alert the moment a customer case is put to you or
        the office replies — plus a red number on the app icon while one is still waiting.
      </div>
      <button style={btn(true)} onClick={turnOn} disabled={busy}>{busy ? 'Just a moment…' : 'Turn on alerts'}</button>
      {msg && (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.8rem', marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> <span>{msg}</span>
        </div>
      )}
    </div>
  );
}
